import { HostedFieldFieldOptions } from 'braintree-web';
import * as Sentry from '@sentry/react';
import { HostedFieldKeys } from './constants';

interface HostedFieldAllEmpty {
    code: 'HOSTED_FIELDS_FIELDS_EMPTY';
    message: string;
    name: 'BraintreeError';
    type: 'CUSTOMER';
}

interface HostedFieldSomeInvalid {
    code: 'HOSTED_FIELDS_FIELDS_INVALID';
    details: { invalidFieldKeys: Set<keyof HostedFieldFieldOptions> };
    message: string;
    name: 'BraintreeError';
    type: 'CUSTOMER';
}

interface HostedFieldCvvInvalid {
    name: 'BraintreeError';
    code: 'HOSTED_FIELDS_TOKENIZATION_CVV_VERIFICATION_FAILED';
    message: string;
    type: 'CUSTOMER';
    details: {
        originalError: {
            error: { message: 'Credit card is invalid' };
            fieldErrors: [
                {
                    field: 'creditCard';
                    fieldErrors: [{ code: string; field: keyof HostedFieldFieldOptions; message: string }];
                },
            ];
        };
    };
}

interface HostedFieldFailedTokenization {
    name: 'BraintreeError';
    code: 'HOSTED_FIELDS_FAILED_TOKENIZATION';
    message: string;
    type: 'CUSTOMER';
    details: {
        originalError: {
            name: 'BraintreeError';
            code: string;
            message: string;
            type: string;
            details: {
                originalError: {
                    error: { message: string };
                    fieldErrors: [
                        {
                            field: string;
                            fieldErrors: [
                                {
                                    code: string;
                                    field: keyof HostedFieldFieldOptions;
                                    message: string;
                                },
                            ];
                        },
                    ];
                };
                httpStatus: number;
            };
        };
    };
}

export type HostedFieldError =
    | HostedFieldAllEmpty
    | HostedFieldSomeInvalid
    | HostedFieldFailedTokenization
    | HostedFieldCvvInvalid;

export type KnownErrorFields = Set<keyof HostedFieldFieldOptions>;

type UnknownError = { type: 'UNKNOWN_ERROR' };

export type ErrorFields = KnownErrorFields | UnknownError;

export function normalizeError(castedError: HostedFieldError): ErrorFields {
    switch (castedError.code) {
        case 'HOSTED_FIELDS_FIELDS_EMPTY':
            return new Set(HostedFieldKeys);
        case 'HOSTED_FIELDS_FIELDS_INVALID':
            return new Set(castedError.details.invalidFieldKeys);
        case 'HOSTED_FIELDS_FAILED_TOKENIZATION':
            return new Set(
                castedError.details.originalError.details.originalError.fieldErrors
                    .map((field) => field.fieldErrors.map((fieldError) => fieldError.field))
                    .flat(),
            );
        case 'HOSTED_FIELDS_TOKENIZATION_CVV_VERIFICATION_FAILED':
            return new Set(
                castedError.details.originalError.fieldErrors
                    .map((field) => field.fieldErrors.map((fieldError) => fieldError.field))
                    .flat(),
            );
        default:
            Sentry.captureException(castedError);
            return { type: 'UNKNOWN_ERROR' };
    }
}

export function isUnknownBraintreeError(error: ErrorFields): error is UnknownError {
    return (error as UnknownError).type === 'UNKNOWN_ERROR';
}
